import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { getStoredUser } from '@/lib/auth';
import { fetchRecordingStream } from './useMyRecordings';
/** Allowed playback speeds, in the order the menu shows them. */
export const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 1.75, 2];
const RATE_KEY = 'apolo:recording:rate';
const POSITION_KEY_PREFIX = 'apolo:recording:pos';
const MIN_RESUME_SECONDS = 90; // don't bother resuming inside the first minute and a half
const NEAR_END_SECONDS = 60; // this close to the end counts as "finished"
const POSITION_TTL_MS = 1000 * 60 * 60 * 24 * 60; // forget positions after 60 days
const SAVE_EVERY_MS = 5_000;
const SELF_HEAL_DEBOUNCE_MS = 4_000;
const STALL_TICK_MS = 3_000;
function positionKey(recordingId) {
    // Scope by account so a shared computer doesn't mix progress between students.
    // The account UUID, not the email: keys are persistent and must not hold PII (LOPD).
    const userId = getStoredUser()?.id ?? 'anon';
    return `${POSITION_KEY_PREFIX}:${userId}:${recordingId}`;
}
/** One-time GC: drop expired/corrupt entries and legacy email-scoped (PII) keys. */
let sweptStalePositions = false;
function sweepStalePositions() {
    if (sweptStalePositions)
        return;
    sweptStalePositions = true;
    try {
        const prefix = `${POSITION_KEY_PREFIX}:`;
        const toRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key || !key.startsWith(prefix))
                continue;
            if (key.includes('@')) {
                toRemove.push(key); // legacy email-scoped key
                continue;
            }
            try {
                const parsed = JSON.parse(localStorage.getItem(key) ?? '');
                if (typeof parsed.at !== 'number' || Date.now() - parsed.at > POSITION_TTL_MS)
                    toRemove.push(key);
            }
            catch {
                toRemove.push(key);
            }
        }
        toRemove.forEach((k) => localStorage.removeItem(k));
    }
    catch {
        /* storage blocked — nothing to sweep */
    }
}
function readSavedPosition(recordingId) {
    try {
        const raw = localStorage.getItem(positionKey(recordingId));
        if (!raw)
            return 0;
        const parsed = JSON.parse(raw);
        if (typeof parsed.t !== 'number' || typeof parsed.at !== 'number')
            return 0;
        if (Date.now() - parsed.at > POSITION_TTL_MS)
            return 0;
        return parsed.t >= MIN_RESUME_SECONDS ? parsed.t : 0;
    }
    catch {
        return 0;
    }
}
function savePosition(recordingId, t, duration) {
    try {
        const key = positionKey(recordingId);
        // Near the end (or a bogus duration) means "watched": clear instead of saving.
        if (!Number.isFinite(t) || t < MIN_RESUME_SECONDS || (Number.isFinite(duration) && duration - t < NEAR_END_SECONDS)) {
            localStorage.removeItem(key);
            return;
        }
        localStorage.setItem(key, JSON.stringify({ t: Math.floor(t), at: Date.now() }));
    }
    catch {
        /* storage full/blocked — losing the resume point is harmless */
    }
}
function clearSavedPosition(recordingId) {
    try {
        localStorage.removeItem(positionKey(recordingId));
    }
    catch {
        /* ignore */
    }
}
function readSavedRate() {
    try {
        const value = Number(localStorage.getItem(RATE_KEY));
        return PLAYBACK_RATES.includes(value) ? value : 1;
    }
    catch {
        return 1;
    }
}
/**
 * Player engine for a recorded class behind short-lived signed URLs.
 *
 * Owns everything that is not presentation:
 * - mint / silent re-mint of the signed URL before it expires (a token is enforced
 *   on EVERY byte-range request, so it must stay valid through a whole class),
 * - self-heal on media error and on a real stall (watchdog on currentTime progress
 *   instead of the `stalled` event, which Safari fires spuriously),
 * - proactive re-mint when the tab becomes visible near/past expiry (background
 *   tabs throttle timers, so the scheduled refresh may not have run),
 * - resume position per recording + per account (localStorage),
 * - persisted playback rate, re-applied after every source swap (a `src` change
 *   resets `playbackRate`).
 */
export function useSecureRecordingPlayer(recordingId) {
    const videoRef = useRef(null);
    const refreshTimer = useRef(null);
    const lastLoadAt = useRef(0);
    const loadingRef = useRef(false);
    const expiresAtRef = useRef(0);
    const rateRef = useRef(readSavedRate());
    const lastSaveAt = useRef(0);
    const lastPos = useRef(null);
    const stallTicks = useRef(0);
    const lastTickTime = useRef(-1);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [buffering, setBuffering] = useState(false);
    const [rate, setRate] = useState(rateRef.current);
    const [resumedFrom, setResumedFrom] = useState(null);
    const clearTimer = () => {
        if (refreshTimer.current) {
            clearTimeout(refreshTimer.current);
            refreshTimer.current = null;
        }
    };
    /** (Re)mint a signed URL and bind it imperatively, optionally keeping position. */
    const load = useCallback(async (preservePosition) => {
        if (loadingRef.current)
            return;
        loadingRef.current = true;
        lastLoadAt.current = Date.now();
        try {
            const stream = await fetchRecordingStream(recordingId);
            const v = videoRef.current;
            if (!v)
                return;
            const resumeAt = preservePosition ? v.currentTime : readSavedPosition(recordingId);
            const wasPlaying = preservePosition ? !v.paused && !v.ended : true;
            v.src = stream.url;
            v.load();
            const onMeta = () => {
                v.removeEventListener('loadedmetadata', onMeta);
                // A source swap resets the playback rate — re-apply the chosen speed.
                v.defaultPlaybackRate = rateRef.current;
                v.playbackRate = rateRef.current;
                if (resumeAt > 0 && (!Number.isFinite(v.duration) || resumeAt < v.duration - NEAR_END_SECONDS)) {
                    try {
                        v.currentTime = resumeAt;
                    }
                    catch {
                        /* seek can fail on some browsers; harmless */
                    }
                    if (!preservePosition)
                        setResumedFrom(resumeAt);
                }
                if (wasPlaying)
                    void v.play().catch(() => undefined);
            };
            v.addEventListener('loadedmetadata', onMeta);
            setError(null);
            setLoading(false);
            // Re-mint at ~85% of the lifetime, before the token expires.
            expiresAtRef.current = new Date(stream.expiresAt).getTime();
            clearTimer();
            const msToExpiry = expiresAtRef.current - Date.now();
            refreshTimer.current = setTimeout(() => void load(true), Math.max(15_000, msToExpiry * 0.85));
        }
        catch (err) {
            setError(err instanceof ApiError ? err.message : 'No se pudo abrir la grabación.');
            setLoading(false);
        }
        finally {
            loadingRef.current = false;
        }
    }, [recordingId]);
    /** A failed range request (e.g. expired token) self-heals by re-minting.
     *  Debounced so a genuinely broken file doesn't loop. */
    const selfHeal = useCallback(() => {
        if (Date.now() - lastLoadAt.current < SELF_HEAL_DEBOUNCE_MS)
            return;
        void load(true);
    }, [load]);
    /** Media error: a source that fails right after a fresh mint is genuinely broken —
     *  surface it (with retry) instead of silently looping; otherwise re-mint. */
    const onMediaError = useCallback(() => {
        if (Date.now() - lastLoadAt.current < SELF_HEAL_DEBOUNCE_MS) {
            setError('No se pudo reproducir el vídeo.');
            setLoading(false);
            return;
        }
        void load(true);
    }, [load]);
    useEffect(() => {
        sweepStalePositions();
        void load(false);
        return () => {
            clearTimer();
            if (lastPos.current)
                savePosition(recordingId, lastPos.current.t, lastPos.current.d);
        };
    }, [load, recordingId]);
    /** Stall watchdog: "playing" but currentTime frozen with no data buffered.
     *  (The `stalled` DOM event is useless for this: Safari fires it spuriously.)
     *  Token-aware: a re-mint swaps the src and throws away the whole buffer, so it
     *  only helps when the token is the plausible culprit (near/past expiry → ~6-9s);
     *  with a known-valid token it's an ordinary rebuffer — only re-mint as a last
     *  resort (~30s frozen, e.g. a silently dead connection). */
    useEffect(() => {
        const id = setInterval(() => {
            const v = videoRef.current;
            if (!v || v.paused || v.ended || v.seeking) {
                stallTicks.current = 0;
                lastTickTime.current = v ? v.currentTime : -1;
                return;
            }
            if (v.currentTime === lastTickTime.current && v.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
                stallTicks.current += 1;
                const nearExpiry = expiresAtRef.current !== 0 && Date.now() > expiresAtRef.current - 60_000;
                if (stallTicks.current >= (nearExpiry ? 2 : 10)) {
                    stallTicks.current = 0;
                    selfHeal();
                }
            }
            else {
                stallTicks.current = 0;
            }
            lastTickTime.current = v.currentTime;
        }, STALL_TICK_MS);
        return () => clearInterval(id);
    }, [selfHeal]);
    /** Background tabs throttle timers: on return, re-mint if the token is about to die. */
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState !== 'visible')
                return;
            if (expiresAtRef.current && expiresAtRef.current - Date.now() < 60_000)
                void load(true);
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [load]);
    const changeRate = useCallback((next) => {
        rateRef.current = next;
        setRate(next);
        try {
            localStorage.setItem(RATE_KEY, String(next));
        }
        catch {
            /* ignore */
        }
        const v = videoRef.current;
        if (v) {
            v.defaultPlaybackRate = next;
            v.playbackRate = next;
        }
    }, []);
    const restartFromBeginning = useCallback(() => {
        clearSavedPosition(recordingId);
        setResumedFrom(null);
        const v = videoRef.current;
        if (v) {
            v.currentTime = 0;
            void v.play().catch(() => undefined);
        }
    }, [recordingId]);
    const retry = useCallback(() => {
        setError(null);
        setLoading(true);
        void load(true);
    }, [load]);
    // Media event handlers the view attaches to the <video> element.
    const onTimeUpdate = useCallback(() => {
        const v = videoRef.current;
        // The <= 0 guard matters: a src swap (silent re-mint) resets the position to 0
        // and fires a timeupdate — saving that would wipe the stored resume point.
        if (!v || !Number.isFinite(v.currentTime) || v.currentTime <= 0)
            return;
        lastPos.current = { t: v.currentTime, d: v.duration };
        const now = Date.now();
        if (now - lastSaveAt.current < SAVE_EVERY_MS)
            return;
        lastSaveAt.current = now;
        savePosition(recordingId, v.currentTime, v.duration);
    }, [recordingId]);
    const onEnded = useCallback(() => {
        lastPos.current = null;
        clearSavedPosition(recordingId);
    }, [recordingId]);
    const onWaiting = useCallback(() => setBuffering(true), []);
    const onPlaying = useCallback(() => setBuffering(false), []);
    const dismissResume = useCallback(() => setResumedFrom(null), []);
    return {
        videoRef,
        loading,
        buffering,
        error,
        rate,
        resumedFrom,
        changeRate,
        restartFromBeginning,
        retry,
        dismissResume,
        handlers: { onTimeUpdate, onEnded, onWaiting, onPlaying, onCanPlay: onPlaying, onError: onMediaError },
    };
}
//# sourceMappingURL=useSecureRecordingPlayer.js.map